import { metricKeys } from '@pulse/metrics';
import {
  AI_ALERT_STANDARD_METRICS_MAP,
  ECO_AI_ALERT_NWR_METRICS,
} from '@src/components/ai-alerts/constants';
import { isAppModule } from '@src/utils/constants';
import { hasImpactedCount } from './utils';

export const validateSensitivityConfigs = (sensitivityData, ecoAiAlert2 = false) => {
  return sensitivityData.some(
    (config) =>
      // check if error is there for even one config, save needs tp be disabled in that case
      !!checkErrors(config, ecoAiAlert2),
  );
};

function ecoAiAlertCheck(currentConfig) {
  // check sustain values
  const { criticalSustain, warningSustain, sustain } = currentConfig;

  const infoIsInteger = Number.isInteger(sustain);
  const warningIsInteger = Number.isInteger(warningSustain);
  const criticalIsInteger = Number.isInteger(criticalSustain);

  if (sustain === null) {
    return 'Info values should be positive integers';
  }

  if (infoIsInteger && warningSustain === null && criticalSustain === null) {
    return '';
  } else if (infoIsInteger && warningIsInteger && criticalSustain === null) {
    return warningSustain > sustain ? '' : 'Warning values should be greater than Info values.';
  } else if (infoIsInteger && warningSustain === null && criticalIsInteger) {
    return criticalSustain > sustain ? '' : 'Critical values should be greater than Info values.';
  } else if (infoIsInteger && warningIsInteger && criticalIsInteger) {
    if (sustain >= warningSustain) {
      return 'Warning values should be greater than Info values.';
    } else if (warningSustain >= criticalSustain) {
      return 'Critical values should be greater than Warning values.';
    }
    return '';
  }

  return '';
}

export const checkErrors = (currentConfig, ecoAiAlert2 = false) => {
  const errorVariable =
    currentConfig.metricKey === metricKeys.ConcurrentPlays
      ? 'Pcp Drop'
      : hasImpactedCount(currentConfig.metricKey, ecoAiAlert2)
        ? 'Impacted Count'
        : 'Impacted Unique';

  let error = '';

  const onlyCheckPersistence =
    isAppModule &&
    !hasImpactedCount(currentConfig.metricKey, ecoAiAlert2) &&
    !ECO_AI_ALERT_NWR_METRICS.includes(currentConfig.metricKey);

  // this is the logic only for eco ai alert 2.0
  if (onlyCheckPersistence) {
    return ecoAiAlertCheck(currentConfig);
  }

  const { app, metricKey } = currentConfig;
  // validation based on app/metrics
  if (app === 'ai' || AI_ALERT_STANDARD_METRICS_MAP.AI.includes(metricKey)) {
    if (hasErrorInAdConfig(currentConfig)) {
      error = 'Required values should be positive integers';
    }
  } else {
    if (hasErrorInWarningConfig(currentConfig, ecoAiAlert2)) {
      error = `Persistence and ${errorVariable} should all be set for Warning alert.`;
    } else if (hasErrorInCriticalConfig(currentConfig, ecoAiAlert2)) {
      error = `Persistence and ${errorVariable} should all be set for Critical alert.`;
    } else if (hasErrorInWarningValues(currentConfig, ecoAiAlert2)) {
      error = 'Warning values should be greater than Info values.';
    } else if (hasErrorInCriticalValues(currentConfig, ecoAiAlert2)) {
      error = 'Critical values should be greater than Info values.';
    } else if (hasErrorInWarningAndCriticalCombination(currentConfig, ecoAiAlert2)) {
      error = 'Critical values should be greater than Warning values.';
    } else if (hasErrorInInfoConfig(currentConfig, ecoAiAlert2)) {
      error = 'Info values should be positive integers';
    }
  }
  return error;
};

// utils for validation
const isInteger = (values: any[] = []) => values.every((v) => Number.isInteger(v));
const isNull = (values: any[] = []) => values.every((v) => v === null);
// Percentage fields (Impacted Unique %, PCP drop, Impacted Flows %) are allowed
// to be decimals. Number.isInteger() treats 1.5 (and IEEE-754 noise like
// 7.000000000000001) as "unset", which falsely trips the
// "Persistence and Impacted Unique should all be set" banner.
const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

// validate only asf and impactedUnqiue for ad metrics
const hasErrorInAdConfig = (currentConfig) => {
  const { asf, impactedUnique } = currentConfig;
  return !(isInteger([asf, impactedUnique]) || isNull([asf, impactedUnique]));
};

const hasErrorInInfoConfig = (currentConfig, ecoAiAlert2 = false) => {
  const {
    sustain,
    impactedUnique,
    impactedUniquePct,
    impactedFlows,
    impactedFlowsPct,
    sustainPcpDrop,
    metricKey,
  } = currentConfig;

  if (metricKey === metricKeys.ConcurrentPlays) {
    return !(Number.isInteger(sustain) && isFiniteNumber(sustainPcpDrop));
  }

  if (hasImpactedCount(metricKey, ecoAiAlert2)) {
    return !(
      Number.isInteger(sustain) &&
      Number.isInteger(impactedFlows) &&
      isFiniteNumber(impactedFlowsPct)
    );
  }

  return !(
    Number.isInteger(sustain) &&
    Number.isInteger(impactedUnique) &&
    isFiniteNumber(impactedUniquePct)
  );
};

const hasErrorInWarningConfig = (currentConfig, ecoAiAlert2 = false) => {
  const {
    warningSustain,
    warningImpactedUnique,
    warningImpactedUniquePct,
    warningImpactedFlows,
    warningImpactedFlowsPct,
    warningPcpDrop,
    metricKey,
  } = currentConfig;

  if (metricKey === metricKeys.ConcurrentPlays) {
    return !(
      (Number.isInteger(warningSustain) && isFiniteNumber(warningPcpDrop)) ||
      (warningSustain === null && warningPcpDrop === null)
    );
  }

  if (hasImpactedCount(metricKey, ecoAiAlert2)) {
    return !(
      (Number.isInteger(warningSustain) &&
        Number.isInteger(warningImpactedFlows) &&
        isFiniteNumber(warningImpactedFlowsPct)) ||
      (warningSustain === null && warningImpactedFlows === null && warningImpactedFlowsPct === null)
    );
  }

  return !(
    (Number.isInteger(warningSustain) &&
      Number.isInteger(warningImpactedUnique) &&
      isFiniteNumber(warningImpactedUniquePct)) ||
    (warningSustain === null && warningImpactedUnique === null && warningImpactedUniquePct === null)
  );
};

const hasErrorInCriticalConfig = (currentConfig, ecoAiAlert2 = false) => {
  const {
    criticalSustain,
    criticalImpactedUnique,
    criticalImpactedUniquePct,
    criticalImpactedFlows,
    criticalImpactedFlowsPct,
    criticalPcpDrop,
    metricKey,
  } = currentConfig;

  if (metricKey === metricKeys.ConcurrentPlays) {
    return !(
      (Number.isInteger(criticalSustain) && isFiniteNumber(criticalPcpDrop)) ||
      (criticalSustain === null && criticalPcpDrop === null)
    );
  }

  if (hasImpactedCount(metricKey, ecoAiAlert2)) {
    return !(
      (Number.isInteger(criticalSustain) &&
        Number.isInteger(criticalImpactedFlows) &&
        isFiniteNumber(criticalImpactedFlowsPct)) ||
      (criticalSustain === null &&
        criticalImpactedFlows === null &&
        criticalImpactedFlowsPct === null)
    );
  }

  return !(
    (Number.isInteger(criticalSustain) &&
      Number.isInteger(criticalImpactedUnique) &&
      isFiniteNumber(criticalImpactedUniquePct)) ||
    (criticalSustain === null &&
      criticalImpactedUnique === null &&
      criticalImpactedUniquePct === null)
  );
};

const hasErrorInWarningValues = (currentConfig, ecoAiAlert2 = false) => {
  const {
    warningSustain,
    warningImpactedUnique,
    warningImpactedUniquePct,
    warningImpactedFlows,
    warningImpactedFlowsPct,
    sustain,
    impactedUnique,
    impactedUniquePct,
    impactedFlows,
    impactedFlowsPct,
    sustainPcpDrop,
    warningPcpDrop,
    metricKey,
  } = currentConfig;

  if (metricKey === metricKeys.ConcurrentPlays) {
    return (
      (warningSustain && warningSustain <= sustain) ||
      (warningPcpDrop && warningPcpDrop <= sustainPcpDrop)
    );
  }

  if (hasImpactedCount(metricKey, ecoAiAlert2)) {
    return (
      (warningSustain && warningSustain <= sustain) ||
      (warningImpactedFlows && warningImpactedFlows <= impactedFlows) ||
      (warningImpactedFlowsPct !== null && warningImpactedFlowsPct < impactedFlowsPct)
    );
  }

  return (
    (warningSustain && warningSustain <= sustain) ||
    (warningImpactedUnique && warningImpactedUnique <= impactedUnique) ||
    (warningImpactedUniquePct !== null && warningImpactedUniquePct < impactedUniquePct)
  );
};

const hasErrorInCriticalValues = (currentConfig, ecoAiAlert2 = false) => {
  const {
    criticalSustain,
    criticalImpactedUnique,
    criticalImpactedUniquePct,
    criticalImpactedFlows,
    criticalImpactedFlowsPct,
    sustain,
    impactedUnique,
    impactedUniquePct,
    impactedFlows,
    impactedFlowsPct,
    criticalPcpDrop,
    sustainPcpDrop,
    metricKey,
  } = currentConfig;

  if (metricKey === metricKeys.ConcurrentPlays) {
    return (
      (criticalSustain && criticalSustain <= sustain) ||
      (criticalPcpDrop && criticalPcpDrop <= sustainPcpDrop)
    );
  }

  if (hasImpactedCount(metricKey, ecoAiAlert2)) {
    return (
      (criticalSustain && criticalSustain <= sustain) ||
      (criticalImpactedFlows && criticalImpactedFlows <= impactedFlows) ||
      (criticalImpactedFlowsPct !== null && criticalImpactedFlowsPct < impactedFlowsPct)
    );
  }

  return (
    (criticalSustain && criticalSustain <= sustain) ||
    (criticalImpactedUnique && criticalImpactedUnique <= impactedUnique) ||
    (criticalImpactedUniquePct !== null && criticalImpactedUniquePct < impactedUniquePct)
  );
};

const hasErrorInWarningAndCriticalCombination = (currentConfig, ecoAiAlert2 = false) => {
  const {
    criticalSustain,
    warningSustain,
    criticalImpactedUnique,
    warningImpactedUnique,
    criticalImpactedUniquePct,
    warningImpactedUniquePct,
    criticalImpactedFlows,
    warningImpactedFlows,
    criticalImpactedFlowsPct,
    warningImpactedFlowsPct,
    metricKey,
    warningPcpDrop,
    criticalPcpDrop,
  } = currentConfig;

  if (metricKey === metricKeys.ConcurrentPlays) {
    return (
      (criticalSustain && warningSustain && criticalSustain <= warningSustain) ||
      (criticalPcpDrop && warningPcpDrop && criticalPcpDrop <= warningPcpDrop)
    );
  }

  if (hasImpactedCount(metricKey, ecoAiAlert2)) {
    return (
      (criticalSustain && warningSustain && criticalSustain <= warningSustain) ||
      (criticalImpactedFlows &&
        warningImpactedFlows &&
        criticalImpactedFlows <= warningImpactedFlows) ||
      (criticalImpactedFlowsPct !== null &&
        warningImpactedFlowsPct !== null &&
        criticalImpactedFlowsPct < warningImpactedFlowsPct)
    );
  }

  return (
    (criticalSustain && warningSustain && criticalSustain <= warningSustain) ||
    (criticalImpactedUnique &&
      warningImpactedUnique &&
      criticalImpactedUnique <= warningImpactedUnique) ||
    (criticalImpactedUniquePct !== null &&
      warningImpactedUniquePct !== null &&
      criticalImpactedUniquePct < warningImpactedUniquePct)
  );
};
